import React, { useState, useEffect, useCallback } from 'react';
import { FiCopy, FiMove, FiX, FiChevronRight, FiChevronDown, FiFolder, FiTarget } from 'react-icons/fi';

const CopyMoveDialog = ({
  show,
  onClose,
  selectedTasks = [],
  allTasks = [],
  allPanels = [], // Neue Prop für alle verfügbaren Panels (auch leere)
  onConfirm,
  singleTask = null // Neue Prop für einzelne Task-Operationen
}) => {
  const [selectedPanel, setSelectedPanel] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Extrahiere Panel-Struktur aus allTasks UND allPanels (um auch leere Panels zu zeigen)
  const getPanelStructure = useCallback(() => {
    const groups = new Map();
    
    // Zuerst alle verfügbaren Panels aus allPanels hinzufügen (auch leere)
    allPanels.forEach(panel => {
      const groupName = panel.groupTitle || 'Unbenannte Gruppe';
      const panelName = panel.title || 'Unbenanntes Panel';
      const panelId = panel.id;
      
      if (!groups.has(groupName)) {
        groups.set(groupName, new Map());
      }
      
      if (!groups.get(groupName).has(panelId)) {
        groups.get(groupName).set(panelId, {
          id: panelId,
          name: panelName,
          taskCount: 0
        });
      }
    });
    
    // Dann Task-Counts aus allTasks hinzufügen
    allTasks.forEach(task => {
      const groupName = task.panelGroupTitle || 'Unbenannte Gruppe';
      const panelId = task.panelId;
      
      if (groups.has(groupName) && groups.get(groupName).has(panelId)) {
        groups.get(groupName).get(panelId).taskCount++;
      }
    });

    return Array.from(groups.entries()).map(([groupName, panels]) => ({
      name: groupName,
      panels: Array.from(panels.values())
    }));
  }, [allTasks, allPanels]);

  const panelStructure = getPanelStructure();

  // Reset beim Öffnen/Schließen des Dialogs
  useEffect(() => {
    if (show) {
      setSelectedPanel(null);
      setExpandedGroups(new Set());
    }
  }, [show]);  if (!show) return null;

  const toggleGroup = (groupIndex) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupIndex)) {
      newExpanded.delete(groupIndex);
    } else {
      newExpanded.add(groupIndex);
    }
    setExpandedGroups(newExpanded);
  };

  const handlePanelSelect = (panelId, panelName, groupName) => {
    setSelectedPanel({ id: panelId, name: panelName, groupName });
  };

  // Bestimme die zu verarbeitenden Tasks
  const tasksToProcess = singleTask ? [singleTask] : selectedTasks;
  const isSingleTaskMode = !!singleTask;

  const handleAction = (isCopy) => {
    if (!selectedPanel) return;
    
    onConfirm({
      operation: isCopy ? 'duplicate' : 'move',
      taskIds: tasksToProcess.map(task => task.id),
      settings: {
        targetPanelId: selectedPanel.id,
        targetPanelName: selectedPanel.name,
        targetGroupName: selectedPanel.groupName,
        createCopy: isCopy
      },
      isSingleTaskMode
    });
    
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        minWidth: '500px',
        maxWidth: '700px',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
      }}>
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            color: '#666',
            borderRadius: '4px'
          }}
          title="Schließen"
        >
          <FiX size={20} />
        </button>

        {/* Modal Header */}
        <h3 style={{ 
          margin: '0 0 16px 0', 
          fontSize: '18px', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          paddingRight: '40px'
        }}>
          <FiTarget />
          Tasks verwalten
        </h3>



        {/* Selected Tasks Info */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            padding: '12px',
            backgroundColor: '#e8f4fd',
            borderRadius: '6px',
            fontSize: '14px'
          }}>
            {isSingleTaskMode ? (
              <>
                <strong>Task:</strong> {singleTask.label}
              </>
            ) : (
              <>
                <strong>{selectedTasks.length} Task{selectedTasks.length !== 1 ? 's' : ''} ausgewählt</strong>
              </>
            )}
          </div>
        </div>

        {/* Panel Selection */}
        <div style={{ marginBottom: '20px', flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>
            Ziel-Panel auswählen:
          </h4>
          
          <div style={{
            border: '1px solid #ddd',
            borderRadius: '6px',
            overflow: 'hidden',
            flex: 1,
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              flex: 1,
              overflowY: 'auto',
              maxHeight: '300px'
            }}>
              {panelStructure.map((group, groupIndex) => (
                <div key={groupIndex}>
                  {/* Group Header */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px',
                      backgroundColor: '#f5f5f5',
                      borderBottom: '1px solid #eee',
                      cursor: 'pointer',
                      fontWeight: '500',
                      fontSize: '14px'
                    }}
                    onClick={() => toggleGroup(groupIndex)}
                  >
                    {expandedGroups.has(groupIndex) ? (
                      <FiChevronDown size={16} style={{ marginRight: '8px' }} />
                    ) : (
                      <FiChevronRight size={16} style={{ marginRight: '8px' }} />
                    )}
                    <FiFolder size={16} style={{ marginRight: '8px', color: '#6c757d' }} />
                    <span>{group.name}</span>
                    <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#666' }}>
                      {group.panels.length} Panel{group.panels.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Panels */}
                  {expandedGroups.has(groupIndex) && (
                    <div>
                      {group.panels.map((panel) => (
                        <div
                          key={panel.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '10px 12px 10px 40px',
                            backgroundColor: selectedPanel?.id === panel.id ? '#e7f3ff' : 'white',
                            borderBottom: '1px solid #f0f0f0',
                            cursor: 'pointer',
                            fontSize: '14px',
                            borderLeft: selectedPanel?.id === panel.id ? '3px solid #007bff' : '3px solid transparent'
                          }}
                          onClick={() => handlePanelSelect(panel.id, panel.name, group.name)}
                          onMouseEnter={(e) => {
                            if (selectedPanel?.id !== panel.id) {
                              e.target.style.backgroundColor = '#f8f9fa';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (selectedPanel?.id !== panel.id) {
                              e.target.style.backgroundColor = 'white';
                            }
                          }}
                        >
                          <FiTarget size={14} style={{ marginRight: '8px', color: '#6c757d' }} />
                          <span>{panel.name}</span>
                          <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#666' }}>
                            {panel.taskCount} Task{panel.taskCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Selected Panel Info */}
          {selectedPanel && (
            <div style={{
              marginTop: '12px',
              padding: '10px',
              backgroundColor: '#e8f5e8',
              borderRadius: '4px',
              fontSize: '14px'
            }}>
              <strong>Ausgewähltes Ziel:</strong> {selectedPanel.groupName} → {selectedPanel.name}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          justifyContent: 'flex-end',
          marginTop: '16px',
          paddingTop: '16px',
          borderTop: '1px solid #eee'
        }}>
          <button
            style={{
              padding: '8px 16px',
              border: '1px solid #6c757d',
              borderRadius: '6px',
              backgroundColor: 'white',
              color: '#6c757d',
              cursor: 'pointer',
              fontSize: '14px'
            }}
            onClick={onClose}
          >
            Abbrechen
          </button>
          <button
            style={{
              padding: '8px 20px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: selectedPanel ? '#28a745' : '#6c757d',
              color: 'white',
              cursor: selectedPanel ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onClick={() => handleAction(true)}
            disabled={!selectedPanel}
          >
            <FiCopy size={16} />
            Kopieren
          </button>
          <button
            style={{
              padding: '8px 20px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: selectedPanel ? '#007bff' : '#6c757d',
              color: 'white',
              cursor: selectedPanel ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onClick={() => handleAction(false)}
            disabled={!selectedPanel}
          >
            <FiMove size={16} />
            Verschieben
          </button>
        </div>
      </div>
    </div>
  );
};

export default CopyMoveDialog;